import { Head } from "@inertiajs/react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { RecentActivityCard } from "@/components/activity/recent-activity-card"
import { BadgePreviewBlock } from "@/components/badges/badge-preview-block"
import { DormantInflectionBand } from "@/components/creator/dormant-inflection-band"
import type { CrewSwitcherStat } from "@/components/crew-switcher"
import { UnverifiedEmailBanner } from "@/components/email-verification/unverified-email-banner"
import { BrandImpactCard } from "@/components/profile/brand-impact-card"
import { CoinsWalletCard } from "@/components/profile/coins-wallet-card"
import { KudosLinkCard } from "@/components/profile/kudos-link-card"
import { MergedXpRankCard } from "@/components/profile/merged-xp-rank-card"
import { ProfileIdentityHead } from "@/components/profile/profile-identity-head"
import { ProfileStreakCard } from "@/components/profile/profile-streak-card"
import { PreviewModeProvider } from "@/components/profile-preview/preview-mode"
import { ProfilePreviewBanner } from "@/components/profile-preview/profile-preview-banner"
import AppLayout from "@/layouts/app-layout"
import type { CreatorProfileProps } from "@/types/xp"

/**
 * OKS-513 — Creator/Profile.tsx (`/profile`) — §11 gamification spine.
 *
 * A redesign of the OKS-48/387/400 scaffold into premium section cards
 * (UX Spec §2 — reading order = visual order):
 *   0. <DormantInflectionBand>  — "welcome back" band (dormant only)
 *   0b. <UnverifiedEmailBanner> — OKS-799, unverified creators only
 *   1. <ProfileIdentityHead>    — brand cover + MomentumRingAvatar + rank
 *                                 patch + crew switcher chip + 🔥 cheers
 *   2. <MergedXpRankCard>       — XP hero (once) + tier journey + reward
 *                                 peek + missions + badges-count
 *   3. <ProfileStreakCard>      — shipped streak data (OKS-517 deepens it)
 *   4. <CoinsWalletCard>        — coins + nearest-reward teaser + Redeem
 *   5. <BrandImpactCard>        — posts · cheers (views deferred — KD-7)
 *   6. <BadgePreviewBlock>      — latest badge medallions (→ §14)
 *   7. <KudosLinkCard>          — data-free link to `/profile/kudos`
 *                                 (OKS-758; no prop — see its docstring)
 *   8. <RecentActivityCard>     — merged recent-activity feed
 *
 * # Per-crew context (PRD §3 — P0)
 *
 * Every stat is scoped server-side to `Current.membership`; switching the
 * active crew (via the head's <CrewSwitcher>, the shipped current-crew
 * mechanism) re-renders this whole page for the chosen crew. No crew id is
 * ever read from the URL/client. The switcher rows are enriched per-crew via
 * `crew_summaries` (creator-only, controller-merged — TRD KD-3).
 *
 * # Brand-admin preview (OKS-400)
 *
 * The same payload (minus `crew_summaries`) renders a brand admin's
 * read-only preview behind <ProfilePreviewBanner>; in preview every
 * affordance is inert (no switcher shown, no navigation).
 */
export default function CreatorProfile({
  brand_name,
  avatar_url,
  creator_name,
  first_name,
  joined_at,
  dormant,
  streak,
  xp_balance,
  recent_badges,
  badges_total,
  current_rank,
  next_rank,
  progress,
  top_tier,
  tiktok_handle,
  recent_activity,
  recent_activity_has_more,
  brand_cover_url,
  xp_weekly_delta,
  xp_to_next,
  rank_ladder,
  missions_done,
  missions_done_this_week,
  badges_earnable_total,
  coin_balance,
  reward_teaser,
  cheers_count,
  impact,
  crew_summaries = [],
  preview = false,
  back_url = null,
}: CreatorProfileProps) {
  const { t } = useTranslation()

  // Per-crew switcher enrichment keyed by brand id (TRD KD-5).
  const crewStats = useMemo<Record<number, CrewSwitcherStat>>(
    () =>
      Object.fromEntries(
        crew_summaries.map((summary) => [
          summary.brand_id,
          {
            rankName: summary.rank_name,
            coins: summary.coins,
            missions: summary.missions,
          },
        ]),
      ),
    [crew_summaries],
  )

  return (
    <AppLayout>
      <Head title={t("xp.profile.title")} />

      {preview && (
        <ProfilePreviewBanner creatorName={creator_name} backUrl={back_url} />
      )}

      <PreviewModeProvider value={preview}>
        <main className="mx-auto flex w-full max-w-2xl flex-col items-stretch gap-5 px-4 py-6">
          {dormant && (
            <DormantInflectionBand
              brandName={brand_name}
              firstName={first_name}
            />
          )}

          {/* OKS-799 — relocated here from the creator shell chrome. Sits
              ABOVE the identity hero so an account-blocking notice is above
              the fold at 375×667 on a page creators visit rarely (UX Spec
              §2/§10.1 — deliberate trade against the premium first
              impression). Below the dormant band when both render (§3e).

              No prop: it self-gates on the SHARED `currentUser` props
              (role/verified/verification_grace_expired), which every page
              already carries. That is also why the brand-admin preview
              needs no special case — an admin fails `role === "creator"`
              and the component returns null, so a preview never shows a
              prompt about the ADMIN's own account.

              No className: this column is `flex-col gap-5`, so the gap is
              already the page's vertical rhythm. */}
          <UnverifiedEmailBanner />

          <ProfileIdentityHead
            name={creator_name}
            avatarUrl={avatar_url}
            handle={tiktok_handle}
            brandName={brand_name}
            brandCoverUrl={brand_cover_url}
            joinedAt={joined_at}
            currentRank={current_rank}
            rankLadder={rank_ladder}
            cheersCount={cheers_count}
            dormant={dormant}
            crewStats={crewStats}
          />

          <MergedXpRankCard
            xpBalance={xp_balance}
            xpWeeklyDelta={xp_weekly_delta}
            xpToNext={xp_to_next}
            currentRank={current_rank}
            nextRank={next_rank}
            progress={progress}
            topTier={top_tier}
            rankLadder={rank_ladder}
            missionsDone={missions_done}
            missionsDoneThisWeek={missions_done_this_week}
            badgesTotal={badges_total}
            badgesEarnableTotal={badges_earnable_total}
          />

          <ProfileStreakCard streak={streak} />

          <CoinsWalletCard
            coinBalance={coin_balance}
            rewardTeaser={reward_teaser}
          />

          <BrandImpactCard brandName={brand_name} impact={impact} />

          <BadgePreviewBlock recent={recent_badges} />

          {/* OKS-758 — data-free entry point to `/profile/kudos`. No prop:
              a content-previewing card would need one from the shared
              `Profile::ProfileProps` builder, which also feeds the
              brand-admin preview. Inert in preview (an admin has no creator
              membership, so the link would 404 them). */}
          <KudosLinkCard />

          <RecentActivityCard
            events={recent_activity}
            hasMore={recent_activity_has_more}
          />
        </main>
      </PreviewModeProvider>
    </AppLayout>
  )
}
